import { makeAuthenticatedRequest } from './authService';

export interface WhiteboardObject {
  id: string;
  type: string; // e.g., "sticky_note"
  x: number;
  y: number;
  width: number;
  height: number;
  color?: string;
  content?: string;
  [key: string]: any; // Allow additional type-specific properties
}

export interface Whiteboard {
  id: number;
  user_id: number;
  name: string;
  objects: WhiteboardObject[];
  created_at: string;
  updated_at: string;
}

/**
 * Get all whiteboards for user
 */
export const getWhiteboards = async (): Promise<Whiteboard[]> => {
  try {
    const response = await makeAuthenticatedRequest('/whiteboards', {
      method: 'GET'
    });
    
    if (response.success && response.data) {
      return Array.isArray(response.data) ? response.data : [];
    }
    
    return [];
  } catch (error: any) {
    if (error.message && (error.message.includes('404') || error.message.includes('not found'))) {
      return [];
    }
    throw error;
  }
};

/**
 * Get a specific whiteboard by ID
 */
export const getWhiteboard = async (id: number): Promise<Whiteboard | null> => {
  try {
    const response = await makeAuthenticatedRequest(`/whiteboards/${id}`, {
      method: 'GET'
    });
    
    if (response.success && response.data) {
      return response.data;
    }
    
    return null;
  } catch (error: any) {
    if (error.message && (error.message.includes('404') || error.message.includes('not found'))) {
      return null;
    }
    throw error;
  }
};

/**
 * Create a new whiteboard
 */
export const createWhiteboard = async (name: string): Promise<Whiteboard> => {
  const response = await makeAuthenticatedRequest('/whiteboards', {
    method: 'POST',
    body: JSON.stringify({
      name: name || 'Untitled Board'
    })
  });
  
  return response.data;
};

/**
 * Update whiteboard (name or entire objects array)
 */
export const updateWhiteboard = async (id: number, data: { name?: string; objects?: WhiteboardObject[] }): Promise<Whiteboard> => {
  const response = await makeAuthenticatedRequest(`/whiteboards/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
  
  return response.data;
};

/**
 * Delete whiteboard
 */
export const deleteWhiteboard = async (id: number): Promise<void> => {
  await makeAuthenticatedRequest(`/whiteboards/${id}`, {
    method: 'DELETE'
  });
};

/**
 * Add object to whiteboard (generic)
 */
export const addObject = async (whiteboardId: number, objectData: WhiteboardObject): Promise<Whiteboard> => {
  const response = await makeAuthenticatedRequest(`/whiteboards/${whiteboardId}/objects`, {
    method: 'POST',
    body: JSON.stringify(objectData)
  });
  
  return response.data;
};

/**
 * Update object in whiteboard
 */
export const updateObject = async (whiteboardId: number, objectId: string, data: Partial<WhiteboardObject>): Promise<Whiteboard> => {
  const response = await makeAuthenticatedRequest(`/whiteboards/${whiteboardId}/objects/${objectId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
  
  return response.data;
};

/**
 * Delete object from whiteboard
 */
export const deleteObject = async (whiteboardId: number, objectId: string): Promise<Whiteboard> => {
  const response = await makeAuthenticatedRequest(`/whiteboards/${whiteboardId}/objects/${objectId}`, {
    method: 'DELETE'
  });
  
  return response.data;
};

